/**
 * @file        src/lib/auth.store.js
 * @description Auth state. Tries silent refresh on bootstrap so a returning
 *              admin lands on the dashboard without re-entering credentials.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-08-05 Claude — bootstrap() now calls client.js's refreshSilently()
 *     (which retries once on REFRESH_RACE) instead of posting to
 *     /auth/refresh directly, so a cold multi-tab load survives the same
 *     cross-tab refresh race the 401 interceptor already handles.
 */
import { create } from 'zustand';
import { post, get, setAccessToken, onUnauthorized, refreshSilently } from '../api/client';

export const useAuth = create((set) => ({
  user: null,
  status: 'idle',     // idle | loading | authed | guest
  error: null,

  async bootstrap() {
    set({ status: 'loading', error: null });
    try {
      const accessToken = await refreshSilently();
      if (accessToken) {
        const me = await get('/auth/me');
        return set({ user: me.data, status: 'authed' });
      }
      set({ user: null, status: 'guest' });
    } catch (_) {
      set({ user: null, status: 'guest' });
    }
  },

  async login(email, password) {
    set({ status: 'loading', error: null });
    try {
      const res = await post('/auth/login', { email, password });
      setAccessToken(res.data.accessToken);
      set({ user: res.data.user, status: 'authed', error: null });
      return true;
    } catch (err) {
      const msg = err.response?.data?.error?.message || 'Login failed';
      set({ user: null, status: 'guest', error: msg });
      return false;
    }
  },

  async logout() {
    try { await post('/auth/logout'); } catch (_) { /* noop */ }
    setAccessToken(null);
    set({ user: null, status: 'guest', error: null });
  },

  forceLogout() {
    setAccessToken(null);
    set({ user: null, status: 'guest' });
  },
}));

// Wire the axios "give up on refresh" handler to the store.
onUnauthorized(() => useAuth.getState().forceLogout());
